"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import type { CouponWithStoreData } from "@/types/couponsWithStoresData";
import CouponModal from "@/components/coupon_popup";
import CouponRevealButton from "@/components/CouponRevealButton";
import { openAffiliateLink } from "@/lib/openAffiliateLink";
import { getStoreLogoAlt } from "@/lib/storeLogo";
import { AdRectangle2 } from "@/components/ads/GptAd";

interface PromoCodesSectionProps {
  coupons: CouponWithStoreData[];
  couponId: string;
}

export default function PromoCodesSection({ coupons, couponId }: PromoCodesSectionProps) {
  const [selectedCoupon, setSelectedCoupon] = useState<CouponWithStoreData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const handleGetCouponClick = (coupon: CouponWithStoreData) => {
    setSelectedCoupon(coupon);
    setIsModalOpen(true);

    if (coupon.couponUrl) {
      openAffiliateLink(coupon.couponUrl, { couponId: coupon._id });
    } else if (coupon.store?.slug && typeof window !== "undefined") {
      const storeSlug = coupon.store.slug;
      window.setTimeout(() => {
        window.location.assign(`/stores/${storeSlug}`);
      }, 1000);
      return;
    }

    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("couponId", coupon._id);
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
    }
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = isModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isModalOpen]);

  // Auto-open modal if couponId exists
  useEffect(() => {
    if (couponId) {
      const coupon = coupons.find(c => c._id === couponId);
      if (coupon) {
        setSelectedCoupon(coupon);
        setIsModalOpen(true);
      }
    }
  }, [couponId, coupons]);

  if (!coupons.length) {
    return (
      <div className="max-w-5xl mx-auto py-3 sm:py-5 px-4 text-center text-purple-700 font-semibold">
        No coupons available.
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-3 sm:py-5 px-4 sm:px-6 lg:px-8">
      <div className="mb-5 flex items-center justify-between gap-4">
        <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">
          Top Coupons & Promo Codes
        </h2>
        <a href="/coupons" aria-label="View all promo codes" className="shrink-0 text-xs font-semibold uppercase text-purple-700 underline underline-offset-4 hover:text-purple-900">
          View All
        </a>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {coupons.map((coupon, idx) => {
          const expiration = coupon.expirationDate
            ? new Date(coupon.expirationDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "No expiry listed";
          const displayStoreName = coupon.store?.name || coupon.storeName || "Unknown Store";

          return (
            <React.Fragment key={coupon._id || idx}>
            <article className="flex min-h-[230px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
              <div className="relative flex h-24 items-center justify-center bg-gray-100 p-3">
                {coupon.store?.image ? (
                  <Image
                    src={`https://www.itscoupons.com${coupon.store.image}`}
                    alt={getStoreLogoAlt(coupon.store?.slug || displayStoreName)}
                    width={180}
                    height={96}
                    sizes="(max-width: 640px) 44vw, 180px"
                    className="h-full w-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <span className="text-3xl font-bold text-purple-700">{displayStoreName.charAt(0)}</span>
                )}
                <span className="absolute left-2 top-2 rounded-sm border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-gray-800">
                  {coupon.discount || "Coupon"} Off
                </span>
              </div>
              <div className="flex flex-1 flex-col p-3">
                <p className="mb-1 truncate text-[10px] font-bold uppercase text-gray-700">{displayStoreName}</p>
                <h3 className="line-clamp-2 min-h-10 text-sm font-semibold leading-5 text-gray-900">{coupon.title}</h3>
                <p className="mt-1 text-xs text-gray-500">Expires: {expiration}</p>
                {coupon.couponCode && (
                  <CouponRevealButton
                    onClick={() => handleGetCouponClick(coupon)}
                    aria-label={`Show code for ${coupon.title}`}
                    couponCode={coupon.couponCode}
                    mobile
                    className="mt-auto pt-3"
                  />
                )}
              </div>
            </article>
            {(idx + 1) % 5 === 0 && idx < coupons.length - 1 && (
              <div className="col-span-full flex justify-center py-2">
                <AdRectangle2 />
              </div>
            )}
            </React.Fragment>
          );
        })}
      </div>

      <CouponModal
        storeName={selectedCoupon?.store?.name}
        storeSlug={selectedCoupon?.store?.slug}
        storeImageUrl={selectedCoupon?.store?.image}
        title={selectedCoupon?.title}
        code={selectedCoupon?.couponCode}
        discount={selectedCoupon?.discount}
        redeemUrl={selectedCoupon?.couponUrl}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
